import {
  call, put, race, fork, take, cancel, cancelled, all, takeLatest, delay, select,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { Task } from 'redux-saga';

import CancelResourceById from 'ia-react-core/~/actions/resource/CancelResourceById';
import CancelResourceByName from 'ia-react-core/~/actions/resource/CancelResourceByName';
import RequestResourceFailureById from 'ia-react-core/~/actions/resource/RequestResourceFailureById';
import getScoped from 'ia-react-core/~/utils/redux/getScoped';
import updateResourceById from 'ia-react-core/~/actions/resource-fn/updateResourceById';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import requestResourceByName from '../../../actions/resource-fn/requestResourceByName';
import requestResourceSuccessById from '../../../actions/resource-fn/requestResourceSuccessById';
import requestResourceSuccessByName from '../../../actions/resource-fn/requestResourceSuccessByName';
import requestResourceFailureById from '../../../actions/resource-fn/requestResourceFailureById';
import requestResourceFailureByName from '../../../actions/resource-fn/requestResourceFailureByName';
import cancelResourceById from '../../../actions/resource-fn/cancelResourceById';
import cancelResourceByName from '../../../actions/resource-fn/cancelResourceByName';
import requestResourceCompletedById from '../../../actions/resource-fn/requestResourceCompletedById';
import requestResourceCompletedByName from '../../../actions/resource-fn/requestResourceCompletedByName';
import { GroupedResourceInfo, ResourceGroupComposedProps, ResourceGroupSuccesses } from './ResourceGroup';

type SuccessfullRequests = Record<number | string, { request: AxiosResponse; name: string }>;
type SuccessfullRequestsOptions = { successfullRequests: SuccessfullRequests };
type Props = ResourceGroupComposedProps<any, any, any> & {
  resolvedDataDst: string;
};

function* cancellableGroupedRequests(props: Props, filteredGroupedResourceInfos: GroupedResourceInfo[], { successfullRequests }: SuccessfullRequestsOptions): any {
  const { runInSequence } = props;

  try {
    for (let i = 0, l = filteredGroupedResourceInfos.length; i < l; i++) {
      const { resourceId: groupedResourceId } = filteredGroupedResourceInfos[i];

      if (!successfullRequests[groupedResourceId]) {
        yield put(requestResourceById(groupedResourceId));

        if (runInSequence) {
          yield take([
            `${requestResourceSuccessById.type}/${groupedResourceId}`,
            `${requestResourceFailureById.type}/${groupedResourceId}`,
          ]);
        }
      }
    }

    if (!runInSequence) {
      yield all(filteredGroupedResourceInfos.map(({ resourceId: groupedResourceId }) => (
        successfullRequests[groupedResourceId] || take([
          `${requestResourceSuccessById.type}/${groupedResourceId}`,
          `${requestResourceFailureById.type}/${groupedResourceId}`,
        ])
      )));
    }
  } catch {
    // Errors are managed by requestResourceGroup, this code should never be reached
  } finally {
    if (yield cancelled()) {
      for (let i = 0, l = filteredGroupedResourceInfos.length; i < l; i++) {
        yield put(cancelResourceById(filteredGroupedResourceInfos[i].resourceId));
      }
    }
  }
}

function* takeNextRequest(successfullRequests: SuccessfullRequests, groupedResourceId: number): any {
  if (successfullRequests[groupedResourceId]) {
    return successfullRequests[groupedResourceId];
  }

  const action = yield take(`${requestResourceSuccessById.type}/${groupedResourceId}`);
  successfullRequests[groupedResourceId] = action;
  return action;
}

function clearSucccessfullRequests(successfullRequests: SuccessfullRequests): any {
  Object.keys(successfullRequests).forEach((groupedResourceId) => {
    delete successfullRequests[groupedResourceId];
  });
}

function* requestResourceGroup(argsGetter: () => Props, { successfullRequests }: SuccessfullRequestsOptions): any {
  const props = argsGetter();
  const {
    resourceId, groupedResourceInfos, name, responseResolvers, store, scope, onSuccess, onCancel, onFailure, onComplete,
  } = props;

  const state = store.getState();
  const filteredGroupedResourceInfos = groupedResourceInfos.filter(({ dataDst, autoRequestIfEmpty }) => {
    if (autoRequestIfEmpty) {
      const data = getScoped(state, dataDst as string, scope);
      return !data;
    }
    return true;
  });

  const backgroundRequest: Task = yield fork(cancellableGroupedRequests, props, filteredGroupedResourceInfos, { successfullRequests });

  const { cancelById, cancelByName, successes, failures }: {
    cancelById: CancelResourceById;
    cancelByName: CancelResourceByName;
    successes: ResourceGroupSuccesses<any>;
    failures: RequestResourceFailureById[];
  } = yield race({
    cancelById: take(`${cancelResourceById.type}/${resourceId}`),
    cancelByName: take(`${cancelResourceByName.type}/${name}`),
    successes: all(filteredGroupedResourceInfos
      .map(({ resourceId: groupedResourceId }) => (
        takeNextRequest(successfullRequests, groupedResourceId)
      ))),
    failures: race(filteredGroupedResourceInfos.map(({ resourceId: groupedResourceId }) => (
      take(`${requestResourceFailureById.type}/${groupedResourceId}`)
    ))),
  });

  if (cancelById || cancelByName) {
    yield cancel(backgroundRequest);

    if (onCancel) {
      yield call(onCancel);
    }
  }

  if (successes) {
    let successesRequests = successes
      .map(({ resourceId: groupedResourceId, request, name: groupedResourceName }) => ({
        resourceId: groupedResourceId,
        name: groupedResourceName,
        request,
      }));

    if (responseResolvers) {
      for (let i = 0, l = responseResolvers.length; i < l; i++) {
        successesRequests = yield call(responseResolvers[i], successesRequests, { store, scope });
      }
    }

    yield put(requestResourceSuccessById(resourceId, successesRequests, { name }));
    yield put(requestResourceSuccessByName(name, successesRequests, { resourceId }));

    if (onSuccess) {
      yield call(onSuccess, successesRequests);
    }

    clearSucccessfullRequests(successfullRequests);
  }

  if (failures) {
    const failuresErrors = failures
      .filter((failure) => failure)
      .map(({ resourceId: groupedResourceId, error }) => ({
        resourceId: groupedResourceId,
        error: error as Error,
      }));

    yield put(requestResourceFailureById(resourceId, failuresErrors));
    yield put(requestResourceFailureByName(name, failuresErrors));

    if (onFailure) {
      yield call(onFailure, failuresErrors);
    }
  }

  yield put(requestResourceCompletedById(resourceId));
  yield put(requestResourceCompletedByName(name));

  if (onComplete) {
    yield call(onComplete);
  }
}

export default function* resourceGroupSaga(argsGetter: () => Props) {
  const { resourceId, name, autoRequest, autoRequestIfEmpty, resolvedDataDst, scope } = argsGetter();

  const options = {
    successfullRequests: {},
  };

  yield takeLatest([
    `${requestResourceById.type}/${resourceId}`,
    `${requestResourceByName.type}/${name}`,
  ], requestResourceGroup, argsGetter, options);

  if (autoRequest) {
    yield delay(0);
    yield put(requestResourceById(resourceId));
  } else if (autoRequestIfEmpty) {
    const data: Object = yield select((state) => getScoped(state, resolvedDataDst, scope));
    if (!data || Object.values(data).some((value) => value === null)) {
      yield delay(0);
      yield put(requestResourceById(resourceId));
    } else {
      yield put(updateResourceById(resourceId, { pending: false }));
    }
  }
}
